/**
 * Dev-only measurement harness for /kanchat (PLAN kanchat-optimizacion F0).
 *
 * Off unless NEXT_PUBLIC_KANCHAT_METRICS=1 (always honored) or the URL has `metrics=1`
 * (only when NODE_ENV !== 'production'). Evaluated once, on the client. Off means every recorder is a no-op. Exposes
 * `window.__kanchatMetrics = { snapshot(), reset(), startedAt }`.
 *
 * Privacy: only the normalized path (no host, no query string, ids replaced by :id),
 * WS event types and Profiler timings are kept. Never bodies, headers or payloads.
 */

// Message events that make the chat list / detail commit (see types.ts WSEventMap).
const WS_MESSAGE_EVENT = 'chat_list_new_message';

export const isKanchatMetricsEnabled: boolean = (() => {
  if (typeof window === 'undefined') return false;
  try {
    return (
      process.env.NEXT_PUBLIC_KANCHAT_METRICS === '1' ||
      (process.env.NODE_ENV !== 'production' &&
        new URLSearchParams(window.location.search).get('metrics') === '1')
    );
  } catch {
    return false;
  }
})();

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isIdSegment = (segment: string) => {
  if (!segment) return false;
  let s = segment;
  try {
    s = decodeURIComponent(segment);
  } catch {}
  return (
    UUID_RE.test(s) ||
    /^\+?\d+$/.test(s) || // numeric ids and phones (any length)
    /\d{4,}/.test(s) || // phones with prefix/suffix (e.g. 569...@c.us)
    s.includes('@') ||
    /^[0-9a-f]{16,}$/i.test(s) || // hashes
    (/\d/.test(s) && /[a-z]/i.test(s) && s.length >= 12) // mixed opaque ids
  );
};

/** Path only: no host, no query/hash, id-like segments replaced by `:id`. */
export const normalizePath = (url: string): string => {
  let path = url;
  const proto = path.indexOf('://');
  if (proto !== -1) {
    const slash = path.indexOf('/', proto + 3);
    path = slash === -1 ? '/' : path.slice(slash);
  }
  path = path.split(/[?#]/)[0];
  return path
    .split('/')
    .map((segment) => (isIdSegment(segment) ? ':id' : segment))
    .join('/');
};

type RequestStat = {
  count: number;
  errors: number;
  durations: number[];
};
type CommitStat = {
  count: number;
  mount: number;
  update: number;
  'nested-update': number;
  totalActualMs: number;
  maxActualMs: number;
};

let startedAt = Date.now();
let requests = new Map<string, RequestStat>();
let wsEvents = new Map<string, number>();
let commits = new Map<string, CommitStat>();

const MAX_SAMPLES = 2000;
const round = (n: number) => Math.round(n * 100) / 100;

const percentile = (sorted: number[], p: number) =>
  sorted.length
    ? sorted[Math.min(sorted.length - 1, Math.floor(p * sorted.length))]
    : 0;

export function recordRequest(entry: {
  method?: string;
  url: string;
  status: number;
  durationMs: number;
  error?: boolean;
}) {
  if (!isKanchatMetricsEnabled) return;
  const key = `${(entry.method ?? 'GET').toUpperCase()} ${normalizePath(entry.url)}`;
  const stat = requests.get(key) ?? { count: 0, errors: 0, durations: [] };
  stat.count += 1;
  if (entry.error || entry.status >= 400) stat.errors += 1;
  stat.durations.push(entry.durationMs);
  if (stat.durations.length > MAX_SAMPLES) stat.durations.shift();
  requests.set(key, stat);
}

export function recordWsEvent(type: string) {
  if (!isKanchatMetricsEnabled) return;
  wsEvents.set(type, (wsEvents.get(type) ?? 0) + 1);
}

export function recordCommit(
  id: string,
  phase: 'mount' | 'update' | 'nested-update',
  actualDuration: number
) {
  if (!isKanchatMetricsEnabled) return;
  const stat = commits.get(id) ?? {
    count: 0,
    mount: 0,
    update: 0,
    'nested-update': 0,
    totalActualMs: 0,
    maxActualMs: 0,
  };
  stat.count += 1;
  stat[phase] += 1;
  stat.totalActualMs += actualDuration;
  stat.maxActualMs = Math.max(stat.maxActualMs, actualDuration);
  commits.set(id, stat);
}

function snapshot() {
  const elapsedSec = (Date.now() - startedAt) / 1000;
  const minutes = Math.max(elapsedSec / 60, 1 / 60);
  const reqOut: Record<string, unknown> = {};
  let total = 0;
  requests.forEach((stat, key) => {
    const sorted = [...stat.durations].sort((a, b) => a - b);
    total += stat.count;
    reqOut[key] = {
      count: stat.count,
      perMin: round(stat.count / minutes),
      p50Ms: round(percentile(sorted, 0.5)),
      p95Ms: round(percentile(sorted, 0.95)),
      errors: stat.errors,
    };
  });
  const commitsOut: Record<string, unknown> = {};
  commits.forEach((stat, id) => {
    commitsOut[id] = {
      count: stat.count,
      phases: {
        mount: stat.mount,
        update: stat.update,
        'nested-update': stat['nested-update'],
      },
      sumActualMs: round(stat.totalActualMs),
      maxActualMs: round(stat.maxActualMs),
    };
  });
  const wsMessages = wsEvents.get(WS_MESSAGE_EVENT) ?? 0;
  const listAndDetail =
    (commits.get('chatList')?.count ?? 0) +
    (commits.get('chatDetail')?.count ?? 0);
  return {
    elapsedSec: round(elapsedSec),
    requests: reqOut,
    totalReqPerMin: round(total / minutes),
    wsEvents: Object.fromEntries(wsEvents),
    commits: commitsOut,
    commitsPerWsMessage: wsMessages ? round(listAndDetail / wsMessages) : null,
  };
}

function reset() {
  startedAt = Date.now();
  requests = new Map();
  wsEvents = new Map();
  commits = new Map();
  api.startedAt = startedAt;
}

const api = { snapshot, reset, startedAt };

if (isKanchatMetricsEnabled) {
  (window as unknown as { __kanchatMetrics: typeof api }).__kanchatMetrics =
    api;
  console.info('[kanchatMetrics] activo: window.__kanchatMetrics');
}
